/** @file Factory for keyframe commands per channel: relative conversion, unwrap, curve, sorted insert. */
import { z } from 'zod';
import { angle } from '@pose2d/math/angle';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { parseOrThrow } from '@pose2d/format/parseOrThrow';
import { throwError } from '@pose2d/format/throwError';
import type { Curve, Doc, Id } from '@pose2d/format/types';
import { defineCommand, type CommandDef } from '../defineCommand';
import type { CommandContext } from '../commandContext';
import { sameJson } from '../animation/sameJson';
import { assertIkChildInherit } from './assertIkChildInherit';
import { KEY_CHANNELS, type AnyKey, type ChannelSpec, type KeyTrack } from './keyChannels';
import { keyGroup } from './keyGroup';
import { relativeCurve } from './relativeCurve';
import { relativeToSetup } from './relativeToSetup';
import { warnIkDriven } from './warnIkDriven';

export type KeyChannel = 'rotation' | 'x' | 'y' | 'scaleX' | 'scaleY' | 'shearX' | 'shearY' | 'inherit' | 'attachment' | 'rgb' | 'alpha' | 'dark' | 'ikMix' | 'ikSoftness' | 'ikBend' | 'ikStretch' | 'ikCompress';
type KeyInput = { animation: string; target: string; channel: KeyChannel; frame: number; value: unknown; curve?: Curve; unwrap?: 'shortest'; relative?: boolean };
type TransformChannel = Parameters<typeof relativeToSetup>[2];

/** Exhaustive by type: adding a KeyChannel without a table row fails to compile. */
const CHANNELS: Readonly<Record<KeyChannel, ChannelSpec>> = KEY_CHANNELS;
const TRANSFORM: readonly KeyChannel[] = ['rotation', 'x', 'y', 'scaleX', 'scaleY', 'shearX', 'shearY'];
const isTransform = (channel: KeyChannel): channel is TransformChannel => TRANSFORM.includes(channel);

/** Curves only on numeric channels; unwrap only on rotation; relative only on bone transform channels (VALIDATION at the offending path). */
function checkOptions(input: KeyInput): void {
  if (input.curve !== undefined && !CHANNELS[input.channel].numeric) {
    throwError('VALIDATION', undefined, { path: 'curve', hint: `${input.channel} keys are discrete; curves apply to numeric channels only` });
  }
  if (input.unwrap !== undefined && input.channel !== 'rotation') throwError('VALIDATION', undefined, { path: 'unwrap', hint: 'unwrap applies to rotation keys only' });
  if (input.relative === true && !isTransform(input.channel)) {
    throwError('VALIDATION', undefined, { path: 'relative', hint: `${input.channel} keys are absolute; relative applies to rotation, x, y, scaleX, scaleY, shearX and shearY` });
  }
}

/**
 * The stored (setup-relative) value: `value` itself with `relative: true` (C7), else converted from absolute; with
 * unwrap, the rotation lies within 180° of the previous key (else the next key, else 0).
 */
function relativeValue(doc: Doc, boneId: Id, input: KeyInput & { channel: TransformChannel }, value: number, keys: readonly AnyKey[]): number {
  const relative = input.relative === true ? value : relativeToSetup(doc, boneId, input.channel, value);
  if (input.unwrap === undefined) return relative;
  const ref = keys.filter((k) => k.f < input.frame).at(-1) ?? keys.find((k) => k.f > input.frame);
  const from = typeof ref?.v === 'number' ? ref.v : 0;
  return from + angle.shortest(from, relative);
}

/**
 * The key as stored: the setup-relative value of a bone transform channel (relativeValue), the curve with its handles
 * converted the same way (relativeCurve; as given with relative: true), and never -0: JSON reads a -0 back as 0, and a
 * resend of that 0 must stay a no-op (T-235 N-6).
 */
function storedKey(doc: Doc, target: Id, input: KeyInput, value: unknown, keys: readonly AnyKey[]): AnyKey {
  const channel = input.channel;
  const raw = isTransform(channel) ? relativeValue(doc, target, { ...input, channel }, value as number, keys) : value;
  const curve = input.curve === undefined || input.relative === true ? input.curve : relativeCurve(doc, target, channel, input.curve);
  return { f: input.frame, v: typeof raw === 'number' ? raw + 0 : raw, ...(curve === undefined ? {} : { curve }) };
}

/** Warns when an attachment key names a placeholder no skin maps on this slot (legal, but probably a typo). */
function warnUnknownPlaceholder(doc: Doc, slotId: Id, value: unknown, ctx: CommandContext): void {
  if (typeof value !== 'string') return;
  const known = Object.values(doc.skins).some((skin) => skin.entries.some((e) => e.slot === slotId && e.placeholder === value));
  if (!known) ctx.warnings.push({ code: 'INFO', message: `no skin maps placeholder "${value}" on this slot yet`, params: { placeholder: value }, path: 'value' });
}

/**
 * Replaces the key at `key.f` in place (keeping its curve unless a new one is given), pushes a key past the last one,
 * and rebuilds only this timeline for a key between two frames: patches stay O(change), never a splice (R17-01).
 * Returns false when the stored key already holds this value and curve (compared structurally): nothing is written,
 * so a key read back and sent again costs no patch.
 */
function upsertKey(track: KeyTrack, prop: string, key: AnyKey): boolean {
  const keys = track[prop];
  if (keys === undefined) {
    track[prop] = [key];
    return true;
  }
  const same = keys.find((k) => k.f === key.f);
  if (same) {
    const newCurve = key.curve !== undefined && !sameJson(same.curve, key.curve);
    if (same.v === key.v && !newCurve) return false;
    same.v = key.v;
    if (newCurve) same.curve = key.curve;
  } else if ((keys.at(-1)?.f ?? -1) < key.f) {
    keys.push(key);
  } else {
    track[prop] = [...keys.filter((k) => k.f < key.f), key, ...keys.filter((k) => k.f > key.f)];
  }
  return true;
}

/** Builds the single `animation.setKey` command; `channel` selects the timeline, `curve` is accepted for numeric channels only (VALIDATION otherwise). */
export function defineKeyCommand(spec: { description: string }): CommandDef<KeyInput> {
  return defineCommand({
    name: 'animation.setKey',
    description: spec.description,
    input: z
      .object({
        animation: z.string().min(1),
        target: z.string().min(1),
        channel: z.enum(Object.keys(CHANNELS) as KeyChannel[]),
        frame: prim.frame,
        value: z.unknown(),
        curve: prim.curve.exactOptional(),
        unwrap: z.literal('shortest').exactOptional(),
        relative: z.boolean().exactOptional(),
      })
      .strict(),
    apply(draft, input, ctx) {
      checkOptions(input);
      const row = CHANNELS[input.channel];
      const animId = ctx.resolve('animation', input.animation);
      const target = ctx.resolve(row.kind, input.target);
      const value: unknown = parseOrThrow(row.value, input.value, 'value');
      if (input.channel === 'inherit') assertIkChildInherit(draft, target, value, input.target);
      const group = keyGroup(draft, animId, row.group);
      const track = (group[target] ??= {});
      const key = storedKey(draft, target, input, value, track[row.prop] ?? []);
      if (input.channel === 'attachment') warnUnknownPlaceholder(draft, target, key.v, ctx);
      if (input.channel === 'rotation') warnIkDriven(draft, animId, target, ctx);
      if (upsertKey(track, row.prop, key)) draft.meta.updatedAt = ctx.now;
    },
  });
}
